import { useState } from "react";
import * as React from "react";
import { useLocation } from "wouter";
import { useAdminBasePath } from "@/lib/adminPath";
import { useOrganization } from "@/contexts/OrganizationContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuTrigger, ContextMenuSeparator } from "@/components/ui/context-menu";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useToast } from "@/hooks/use-toast";
import { Plus, Edit, Trash2, ExternalLink, ChevronUp, ChevronDown, Settings, ChevronRight, MoreHorizontal, AlertTriangle, Link2, UserPlus, Loader2, Bot } from "lucide-react";
import { SiQuickbooks } from "react-icons/si";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, getApiPath } from "@/lib/queryClient";
import { accountFormSchema, accountUpdateBody, type AccountForm } from "@/lib/accountForm";
import { dashedPhone, groupAccountsBody, phoneDigits } from "@/lib/accountPhone";
import { CUSTOMER_TYPES_PATH, type CustomerTypeSummary } from "@/lib/customerTypes";
import { OrderSlip } from "@/components/order-slip";
import { StartClydeThreadDialog, type StartClydeThreadAccount } from "@/components/start-clyde-thread-dialog";
import { AGENT_THREADS_PATH, consoleThreadPath, type AgentThreadSummary } from "@/lib/clydeThread";

interface QBCustomerMatch {
  id: string;
  displayName: string;
  companyName?: string;
  email?: string;
  phone?: string;
  matchType: 'exact' | 'strong' | 'partial';
  matchReasons?: string[];
  score?: number;
}

// A new account's form. Every reset to "new" names these values: a bare form.reset()
// goes back to the last values the form was reset WITH, which after an edit are that
// account's — the Create dialog then opened holding another account's details.
const newAccountForm: AccountForm = {
  name: "",
  phone: "",
  customUrl: "",
  isActive: true,
  customMenu: {
    categories: {},
    products: {}
  }
};

interface Account {
  id: number;
  name: string;
  phone: string;
  emailAddress?: string;
  customUrl?: string;
  isActive: boolean;
  customMenu?: {
    categories: Record<string, boolean>;
    products: Record<string, boolean>;
  };
  createdAt: string;
  parentAccountId?: number | null;
  isGroupParent?: boolean;
  // BRIEF-account-request-flow: 'pending' = buyer self-requested, awaiting admin review
  status?: 'active' | 'pending';
  requesterName?: string | null;
  requestedAt?: string | null;
  customerTypeId?: number | null;
}

interface Product {
  id: number;
  name: string;
  categoryId: number;
  isActive: boolean;
}

interface Category {
  id: number;
  name: string;
  icon: string;
  isActive: boolean;
  sortOrder: number;
}

/** POST /quickbooks/sync/refresh-phones (server/services/accountPhones.ts). */
interface PhoneRefreshResult {
  linked: number;
  filled: number;
  filledWithPin: { accountId: number; name: string }[];
  upToDate: number;
  differs: { accountId: number; name: string; current: string; quickbooks: string }[];
  skippedShared: { accountId: number; name: string }[];
  skippedGroup: { accountId: number; name: string }[];
  noPhoneInQuickBooks: number;
  notInQuickBooks: number;
}

const formatPhone = (digits: string) => `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;

export default function AccountManagement() {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState<Account | null>(null);
  const [statusPopoverOpen, setStatusPopoverOpen] = useState<number | null>(null);
  const [selectedAccounts, setSelectedAccounts] = useState<Set<number>>(new Set());
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [mergeTargetAccount, setMergeTargetAccount] = useState<number | null>(null);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [deletionInfo, setDeletionInfo] = useState<{ canDelete: number[]; cannotDelete: { id: number; name: string; orderCount: number }[] }>({ canDelete: [], cannotDelete: [] });
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [groupParentName, setGroupParentName] = useState("");
  const [massTextDialogOpen, setMassTextDialogOpen] = useState(false);
  const [phoneRefresh, setPhoneRefresh] = useState<PhoneRefreshResult | null>(null);
  const [massTextMessage, setMassTextMessage] = useState("");
  const [groupPhone, setGroupPhone] = useState("");
  const [qbLinkDialogOpen, setQbLinkDialogOpen] = useState(false);
  const [clydeAccount, setClydeAccount] = useState<StartClydeThreadAccount | null>(null);
  const [qbLinkingAccount, setQbLinkingAccount] = useState<{ id: number; name: string; phone: string } | null>(null);
  const [qbCustomerMatches, setQbCustomerMatches] = useState<QBCustomerMatch[]>([]);
  const [qbLinkSelection, setQbLinkSelection] = useState<string>('skip');
  const [isLoadingQbMatches, setIsLoadingQbMatches] = useState(false);
  const [isLinkingToQb, setIsLinkingToQb] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [, setLocation] = useLocation();
  const adminBase = useAdminBasePath();
  const { orgSlug } = useOrganization();

  const { data: accounts, isLoading } = useQuery<Account[]>({
    queryKey: ['/api/accounts'],
  });

  // Clyde: the menu action shows only where the org's agent is on (same gate as the
  // Clyde tab), and the badge reads the org's live threads in one bounded call.
  const { data: agentSettings } = useQuery<{ agentEnabled?: boolean }>({
    queryKey: ["/api/agent/settings"],
    staleTime: 60_000,
    retry: false,
  });
  const agentEnabled = !!agentSettings?.agentEnabled;
  const { data: agentThreads = [] } = useQuery<AgentThreadSummary[]>({
    queryKey: [AGENT_THREADS_PATH],
    enabled: agentEnabled,
    retry: false,
  });
  const threadByAccount = React.useMemo(() => {
    const m = new Map<number, AgentThreadSummary>();
    for (const t of agentThreads) if (t.accountId != null && !m.has(t.accountId)) m.set(t.accountId, t);
    return m;
  }, [agentThreads]);

  const { data: orders = [] } = useQuery<any[]>({
    queryKey: ["/api/orders", { includeItems: false, limit: 200 }],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/orders?includeItems=false&limit=200");
      return res.json();
    },
  });

  const { data: products = [] } = useQuery<Product[]>({
    queryKey: ['/api/products'],
  });

  // Fetch categories (ordered by sortOrder for consistent ordering across pages)
  const { data: categories = [] } = useQuery<Category[]>({
    queryKey: ['/api/categories'],
  });

  const [selectedOrder, setSelectedOrder] = useState<any | null>(null);
  const [isOrderSlipOpen, setIsOrderSlipOpen] = useState(false);
  // Customer types: a Type column, only in an org that has any.
  const { data: customerTypes = [] } = useQuery<CustomerTypeSummary[]>({ queryKey: [getApiPath(CUSTOMER_TYPES_PATH)] });
  const customerTypeNames = React.useMemo(
    () => new Map(customerTypes.map((t) => [t.id, t.name])),
    [customerTypes],
  );
  const showTypeColumn = customerTypes.length > 0;

  const [sortConfig, setSortConfig] = useState<{key: string, direction: 'asc' | 'desc'} | null>({ key: 'name', direction: 'asc' });
  const [showOnlyRequested, setShowOnlyRequested] = useState(false);
  const [menuConfigOpen, setMenuConfigOpen] = useState<Record<number, boolean>>({});

  // Sort accounts based on configuration, keeping grouped accounts together
  const sortedAccounts = React.useMemo(() => {
    if (!accounts) return [];
    
    const sortFn = (a: Account, b: Account) => {
      if (!sortConfig) return 0;
      let aValue: any = a[sortConfig.key as keyof Account];
      let bValue: any = b[sortConfig.key as keyof Account];
      
      if (sortConfig.key === 'lastOrder') {
        const aOrders = orders.filter(order => order.accountId === a.id);
        const bOrders = orders.filter(order => order.accountId === b.id);
        aValue = aOrders.length > 0 ? Math.max(...aOrders.map(o => new Date(o.createdAt).getTime())) : 0;
        bValue = bOrders.length > 0 ? Math.max(...bOrders.map(o => new Date(o.createdAt).getTime())) : 0;
      } else if (sortConfig.key === 'customerType') {
        // Typed accounts by type name; Default (and group parents) are blank, so last either way.
        const typeName = (acct: Account) =>
          (acct.customerTypeId != null && customerTypeNames.get(acct.customerTypeId)) || '';
        aValue = typeName(a);
        bValue = typeName(b);
      } else if (sortConfig.key === 'createdAt') {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      }

      // Blank values (e.g. accounts imported from QuickBooks with no phone) sort last either way
      const aBlank = aValue == null || aValue === '';
      const bBlank = bValue == null || bValue === '';
      if (aBlank || bBlank) return aBlank === bBlank ? 0 : aBlank ? 1 : -1;

      if (typeof aValue === 'string') aValue = aValue.toLowerCase();
      if (typeof bValue === 'string') bValue = bValue.toLowerCase();

      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    };

    // "Requested" filter: only pending requests (a pending group's children are pending too)
    const visible = showOnlyRequested ? accounts.filter(a => a.status === 'pending') : accounts;

    // Separate parents, children, and standalone accounts
    const parentAccounts = visible.filter(a => a.isGroupParent);
    const childAccounts = visible.filter(a => a.parentAccountId);
    const standaloneAccounts = visible.filter(a => !a.isGroupParent && !a.parentAccountId);

    // Build grouped list: parent followed by its children, then standalone accounts
    const result: Account[] = [];
    const sortedParents = [...parentAccounts].sort(sortFn);
    const sortedStandalone = [...standaloneAccounts].sort(sortFn);
    
    // Interleave parents (with children) and standalone accounts by sort order,
    // with pending requests pinned to the top so they can't be missed.
    const allTopLevel = [...sortedParents, ...sortedStandalone].sort(sortFn)
      .sort((a, b) => Number(b.status === 'pending') - Number(a.status === 'pending'));
    
    for (const acct of allTopLevel) {
      result.push(acct);
      if (acct.isGroupParent) {
        const children = childAccounts.filter(c => c.parentAccountId === acct.id).sort(sortFn);
        result.push(...children);
      }
    }

    return result;
  }, [accounts, sortConfig, orders, showOnlyRequested, customerTypeNames]);

  const requestedCount = React.useMemo(
    () => (accounts ?? []).filter(a => a.status === 'pending' && !a.parentAccountId).length,
    [accounts],
  );

  // Bulk operations helpers
  const toggleAccountSelection = (accountId: number) => {
    const newSelection = new Set(selectedAccounts);
    if (newSelection.has(accountId)) {
      newSelection.delete(accountId);
    } else {
      newSelection.add(accountId);
    }
    setSelectedAccounts(newSelection);
  };

  const toggleAllAccounts = () => {
    if (selectedAccounts.size === sortedAccounts.length) {
      setSelectedAccounts(new Set());
    } else {
      setSelectedAccounts(new Set(sortedAccounts.map(a => a.id)));
    }
  };

  const selectedAccountsData = React.useMemo(() => {
    return sortedAccounts.filter(a => selectedAccounts.has(a.id));
  }, [sortedAccounts, selectedAccounts]);

  const canMakeActive = selectedAccountsData.length > 0 && selectedAccountsData.every(a => !a.isActive && a.status !== 'pending');
  const canMakeInactive = selectedAccountsData.length > 0 && selectedAccountsData.every(a => a.isActive && a.status !== 'pending');

  const massTextRecipientInfo = React.useMemo(() => {
    const phoneMap = new Map<string, string[]>();
    const noPhone: string[] = [];

    for (const acct of selectedAccountsData) {
      let phone = acct.phone;
      if (acct.parentAccountId) {
        const parent = accounts?.find(a => a.id === acct.parentAccountId);
        phone = parent?.phone || acct.phone;
      }

      const cleaned = phone?.replace(/\D/g, '') || '';
      if (cleaned.length < 10) {
        noPhone.push(acct.name);
        continue;
      }

      const existing = phoneMap.get(cleaned) || [];
      existing.push(acct.name);
      phoneMap.set(cleaned, existing);
    }

    return {
      uniquePhones: phoneMap.size,
      totalSelected: selectedAccountsData.length,
      noPhone,
    };
  }, [selectedAccountsData, accounts]);

  const massTextMutation = useMutation({
    mutationFn: async ({ accountIds, message }: { accountIds: number[]; message: string }) => {
      const res = await apiRequest("POST", "/api/accounts/bulk-sms", { accountIds, message });
      return res.json();
    },
    onSuccess: (data) => {
      toast({
        title: "Messages Sent",
        description: `${data.sent} sent, ${data.failed} failed${data.skipped > 0 ? `, ${data.skipped} skipped (no phone)` : ''}`,
      });
      setMassTextDialogOpen(false);
      setMassTextMessage("");
      setSelectedAccounts(new Set());
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to send messages",
        variant: "destructive",
      });
    },
  });

  const getLastOrderInfo = (accountId: number) => {
    const accountOrders = orders.filter(order => order.accountId === accountId);
    if (accountOrders.length === 0) return <span className="text-gray-400">No orders</span>;
    
    const lastOrder = accountOrders.reduce((latest, current) => 
      new Date(current.createdAt) > new Date(latest.createdAt) ? current : latest
    );
    
    return (
      <button
        onClick={() => {
          setSelectedOrder(lastOrder);
          setIsOrderSlipOpen(true);
        }}
        className="text-cn-brand-blue hover:text-cn-brand-blue-hover hover:underline cursor-pointer"
      >
        {new Date(lastOrder.createdAt).toLocaleDateString()}
      </button>
    );
  };

  const handleBulkStatusUpdate = async (isActive: boolean) => {
    const accountIds = Array.from(selectedAccounts);
    bulkUpdateStatusMutation.mutate({ accountIds, isActive });
  };

  const handleMergeAccounts = async () => {
    if (!mergeTargetAccount || selectedAccounts.size < 2) return;
    
    const sourceAccountIds = Array.from(selectedAccounts).filter(id => id !== mergeTargetAccount);
    mergeAccountsMutation.mutate({ targetAccountId: mergeTargetAccount, sourceAccountIds });
  };

  const handleBulkDelete = async (explicitIds?: number[]) => {
    const idsToDelete = explicitIds || Array.from(selectedAccounts);
    const checkPromises = idsToDelete.map(async (id) => {
      try {
        const response = await apiRequest("GET", `/api/accounts/${id}/can-delete`);
        const data = await response.json();
        const account = accounts?.find(a => a.id === id);
        return { id, canDelete: data.canDelete, orderCount: data.orderCount || 0, name: account?.name || '' };
      } catch {
        return { id, canDelete: false, orderCount: 0, name: '' };
      }
    });
    
    const results = await Promise.all(checkPromises);
    const canDelete = results.filter(r => r.canDelete).map(r => r.id);
    const cannotDelete = results.filter(r => !r.canDelete).map(r => ({ id: r.id, name: r.name, orderCount: r.orderCount }));
    
    setDeletionInfo({ canDelete, cannotDelete });
    setBulkDeleteDialogOpen(true);
  };

  const confirmBulkDelete = async () => {
    if (deletionInfo.canDelete.length > 0) {
      bulkDeleteMutation.mutate(deletionInfo.canDelete);
    }
  };

  const handleStatusToggle = async (accountId: number, newStatus: boolean) => {
    try {
      await apiRequest("PATCH", `/api/accounts/${accountId}/status`, { isActive: newStatus });
      queryClient.invalidateQueries({ queryKey: ['/api/accounts'] });
      setStatusPopoverOpen(null); // Close the popover
      toast({
        title: "Account status updated",
        description: `Account has been ${newStatus ? 'activated' : 'deactivated'}.`,
      });
    } catch (error: any) {
      toast({
        title: "Failed to update status",
        description: error.message || "Please try again later.",
        variant: "destructive",
      });
    }
  };

  const handleSort = (key: string) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // An edit is checked against the account as stored (accountFormSchema), so the
  // resolver follows the account being edited.
  const resolver = React.useMemo(() => zodResolver(accountFormSchema(editingAccount)), [editingAccount]);
  const form = useForm<AccountForm>({
    resolver,
    defaultValues: newAccountForm
  });

  // Auto-generate custom URL from name
  const generateCustomUrl = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, '') // Remove special characters
      .replace(/\s+/g, '-') // Replace spaces with dashes
      .replace(/-+/g, '-') // Replace multiple dashes with single dash
      .replace(/^-|-$/g, ''); // Remove leading/trailing dashes
  };

  // Keep the URL shown in step with the name. A new account's URL is its name's slug.
  // An existing account shows its stored URL until the name is changed: the server
  // replaces the URL on a rename, and only then.
  const watchedName = form.watch("name");
  const storedUrl = editingAccount?.customUrl || "";
  const isRename = !!editingAccount && !!watchedName?.trim() && watchedName.trim() !== editingAccount.name.trim();
  React.useEffect(() => {
    if (editingAccount && !isRename) {
      form.setValue("customUrl", storedUrl);
    } else if (watchedName) {
      const generatedUrl = generateCustomUrl(watchedName);
      form.setValue("customUrl", generatedUrl);
    }
  }, [watchedName, form, editingAccount, isRename, storedUrl]);

  // The URL the dialog spells out, and whether saving would replace the stored one.
  const shownUrl = !editingAccount ? form.watch("customUrl") : isRename ? generateCustomUrl(watchedName) : storedUrl;
  const urlWillChange = isRename && shownUrl !== storedUrl;

  const createMutation = useMutation({
    mutationFn: async (data: AccountForm) => {
      const res = await apiRequest("POST", "/api/accounts", data);
      return res.json();
    },
    onSuccess: async (data) => {
      queryClient.invalidateQueries({ queryKey: ['/api/accounts'] });
      setDialogOpen(false);
      form.reset(newAccountForm);
      toast({
        title: "Success",
        description: "Account created successfully"
      });
      
      // Check if QuickBooks is connected and offer to link the new account
      try {
        const authResponse = await apiRequest("GET", "/api/quickbooks/auth-status");
        if (!authResponse.ok) {
          console.log('QB auth status check failed:', authResponse.status);
        } else {
          const authData = await authResponse.json();
          
          if (authData.hasValidToken) {
            // Fetch QB customer matches for the new account
            setIsLoadingQbMatches(true);
            const matchResponse = await apiRequest("POST", "/api/quickbooks/find-customer-matches", {
              name: data.name,
              phone: data.phone?.replace(/\D/g, '') || ''
            });
            setIsLoadingQbMatches(false);
            
            if (!matchResponse.ok) {
              console.log('QB customer match search failed:', matchResponse.status);
              toast({
                title: "QuickBooks",
                description: "Could not check for matching customers in QuickBooks. You can link this account later from the account page.",
              });
            } else {
              const matchData = await matchResponse.json();
              
              if (matchData.matches && matchData.matches.length > 0) {
                // Found matches - show linking dialog
                setQbLinkingAccount({ id: data.id, name: data.name, phone: data.phone });
                setQbCustomerMatches(matchData.matches);
                setQbLinkSelection('skip');
                setQbLinkDialogOpen(true);
                return; // Don't navigate yet - wait for user decision
              }
            }
          }
        }
      } catch (error) {
        // QB not connected or error fetching matches - just proceed
        console.log('QB linking check failed:', error);
        setIsLoadingQbMatches(false);
      }
      
      // No QB matches or QB not connected - navigate to the full account page
      setLocation(`${adminBase}/accounts/${data.id}`);
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create account",
        variant: "destructive"
      });
    }
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: ReturnType<typeof accountUpdateBody> }) => {
      const res = await apiRequest("PATCH", `/api/accounts/${id}`, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/accounts'] });
      setDialogOpen(false);
      setEditingAccount(null);
      form.reset(newAccountForm);
      toast({
        title: "Success",
        description: "Account updated successfully"
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update account",
        variant: "destructive"
      });
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await apiRequest("DELETE", `/api/accounts/${id}`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/accounts'] });
      toast({
        title: "Success",
        description: "Account deleted successfully"
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete account",
        variant: "destructive"
      });
    }
  });

  const bulkUpdateStatusMutation = useMutation({
    mutationFn: async ({ accountIds, isActive }: { accountIds: number[], isActive: boolean }) => {
      const promises = accountIds.map(id => 
        apiRequest("PATCH", `/api/accounts/${id}/status`, { isActive })
      );
      return Promise.all(promises);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/accounts'] });
      toast({
        title: "Accounts Updated",
        description: `Successfully updated ${selectedAccounts.size} account(s)`
      });
      setSelectedAccounts(new Set());
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update accounts",
        variant: "destructive"
      });
    }
  });

  const mergeAccountsMutation = useMutation({
    mutationFn: async ({ targetAccountId, sourceAccountIds }: { targetAccountId: number, sourceAccountIds: number[] }) => {
      return apiRequest("POST", "/api/accounts/merge", { targetAccountId, sourceAccountIds });
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ['/api/accounts'] });
      queryClient.invalidateQueries({ queryKey: ['/api/orders'] });
      toast({
        title: "Accounts Merged",
        description: data?.message || "Successfully merged accounts"
      });
      setSelectedAccounts(new Set());
      setMergeDialogOpen(false);
      setMergeTargetAccount(null);
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to merge accounts",
        variant: "destructive"
      });
    }
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: async (accountIds: number[]) => {
      const promises = accountIds.map(id => 
        apiRequest("DELETE", `/api/accounts/${id}`)
      );
      return Promise.all(promises);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/accounts'] });
      toast({
        title: "Accounts Deleted",
        description: `Successfully deleted ${deletionInfo.canDelete.length} account(s)`
      });
      setSelectedAccounts(new Set());
      setBulkDeleteDialogOpen(false);
      setDeletionInfo({ canDelete: [], cannotDelete: [] });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete accounts",
        variant: "destructive"
      });
    }
  });

  const handleSubmit = (data: AccountForm) => {
    if (editingAccount) {
      // An edit sends only what the admin changed (accountUpdateBody) — and nothing at
      // all when they changed nothing.
      const changes = accountUpdateBody(editingAccount, data);
      if (Object.keys(changes).length === 0) {
        setDialogOpen(false);
        return;
      }
      updateMutation.mutate({ id: editingAccount.id, data: changes });
    } else {
      // Strip formatting from phone number before sending to backend
      createMutation.mutate({
        ...data,
        phone: data.phone.replace(/\D/g, '') // Remove all non-digits
      });
    }
  };

  const handleQbLinkConfirm = async () => {
    if (!qbLinkingAccount) return;

    // If user selected skip, just navigate
    if (qbLinkSelection === 'skip') {
      setQbLinkDialogOpen(false);
      setQbLinkingAccount(null);
      setQbCustomerMatches([]);
      setLocation(`${adminBase}/accounts/${qbLinkingAccount.id}`);
      return;
    }

    // Link the account to the selected QB customer
    setIsLinkingToQb(true);
    try {
      await apiRequest("PATCH", `/api/accounts/${qbLinkingAccount.id}`, {
        quickbooksCustomerId: qbLinkSelection
      });
      
      queryClient.invalidateQueries({ queryKey: ['/api/accounts'] });
      
      const selectedMatch = qbCustomerMatches.find(m => m.id === qbLinkSelection);
      toast({
        title: "Account linked to QuickBooks",
        description: `Linked to "${selectedMatch?.displayName || 'QuickBooks customer'}"`
      });
      
      setQbLinkDialogOpen(false);
      setQbLinkingAccount(null);
      setQbCustomerMatches([]);
      setLocation(`${adminBase}/accounts/${qbLinkingAccount.id}`);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to link account",
        variant: "destructive"
      });
    } finally {
      setIsLinkingToQb(false);
    }
  };

  const handleEdit = (account: Account) => {
    setEditingAccount(account);

    // Reset form completely
    form.reset(newAccountForm);

    // Set form values individually to ensure they're applied. Everything the dialog has
    // a field for is loaded as stored — a field left out opens empty and is saved empty.
    // The account's status and menu have no field here and are not loaded or sent.
    setTimeout(() => {
      form.reset({
        ...newAccountForm,
        name: account.name,
        phone: account.phone ?? "",
        emailAddress: account.emailAddress ?? "",
        customUrl: account.customUrl ?? ""
      });
    }, 50);

    setDialogOpen(true);
  };

  const handleDelete = (id: number) => {
    if (confirm("Are you sure you want to delete this account?")) {
      deleteMutation.mutate(id);
    }
  };

  // Helper functions for menu configuration
  const getProductsByCategory = (categoryId: number) => {
    return products.filter(product => product.categoryId === categoryId);
  };

  const handleCategoryToggle = (categoryId: string, enabled: boolean) => {
    const currentMenu = form.getValues("customMenu") || { categories: {}, products: {} };
    const productsInCategory = getProductsByCategory(parseInt(categoryId));
    
    const updatedMenu = {
      ...currentMenu,
      categories: {
        ...currentMenu.categories,
        [categoryId]: enabled
      },
      products: {
        ...currentMenu.products,
        ...productsInCategory.reduce((acc, product) => {
          acc[product.id.toString()] = enabled;
          return acc;
        }, {} as Record<string, boolean>)
      }
    };
    
    form.setValue("customMenu", updatedMenu);
  };

  const handleProductToggle = (productId: string, enabled: boolean) => {
    const currentMenu = form.getValues("customMenu") || { categories: {}, products: {} };
    const product = products.find(p => p.id.toString() === productId);
    
    if (!product) return;
    
    const updatedMenu = {
      ...currentMenu,
      products: {
        ...currentMenu.products,
        [productId]: enabled
      }
    };
    
    // Check if this affects the category status
    const productsInCategory = getProductsByCategory(product.categoryId);
    const enabledProductsInCategory = productsInCategory.filter(p => 
      updatedMenu.products[p.id.toString()]
    );
    
    if (enabledProductsInCategory.length === productsInCategory.length) {
      updatedMenu.categories[product.categoryId.toString()] = true;
    } else if (enabledProductsInCategory.length === 0) {
      updatedMenu.categories[product.categoryId.toString()] = false;
    } else {
      updatedMenu.categories[product.categoryId.toString()] = false; // Partially selected
    }
    
    form.setValue("customMenu", updatedMenu);
  };

  // Initialize default menu config for new accounts
  React.useEffect(() => {
    if (!editingAccount && products.length > 0) {
      const defaultMenuConfig = {
        categories: categories.reduce((acc, category) => {
          acc[category.id.toString()] = true;
          return acc;
        }, {} as Record<string, boolean>),
        products: products.reduce((acc, product) => {
          acc[product.id.toString()] = true;
          return acc;
        }, {} as Record<string, boolean>)
      };
      form.setValue("customMenu", defaultMenuConfig);
    }
  }, [products, editingAccount, form]);

  // Fills placeholder phones (0000000…) from QuickBooks — mobile first — and never
  // overwrites a real number; anything it left alone is listed in the dialog.
  const refreshPhones = useMutation({
    mutationFn: async (): Promise<PhoneRefreshResult> => (await apiRequest("POST", "/api/quickbooks/sync/refresh-phones")).json(),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['/api/accounts'] });
      setPhoneRefresh(result);
    },
    onError: (error: any) => {
      let message = "Nothing was changed. Please try again.";
      try {
        const body = JSON.parse(String(error?.message ?? "").replace(/^\d+:\s*/, ""));
        if (body?.message) message = body.message;
      } catch {
        // keep the generic message
      }
      toast({ title: "Couldn't refresh phone numbers", description: message, variant: "destructive" });
    },
  });

  return (
    <div className="max-w-7xl mx-auto p-3 sm:p-6">
            <Card className="border border-gray-200 shadow-sm">
        <CardHeader className="border-b border-gray-100 bg-gray-50/50 py-3 px-4">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 w-8 p-0 bg-white hover:bg-gray-100 border border-gray-200"
                        data-testid="button-actions"
                      >
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="w-48">
                      <DropdownMenuItem 
                        onClick={!canMakeActive ? undefined : () => handleBulkStatusUpdate(true)}
                        className={!canMakeActive ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
                        disabled={!canMakeActive}
                      >
                        Make Active
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={!canMakeInactive ? undefined : () => handleBulkStatusUpdate(false)}
                        className={!canMakeInactive ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
                        disabled={!canMakeInactive}
                      >
                        Make Inactive
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={selectedAccounts.size < 2 ? undefined : () => setMergeDialogOpen(true)}
                        className={selectedAccounts.size < 2 ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
                        disabled={selectedAccounts.size < 2}
                      >
                        Merge Accounts
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => {
                          const selectable = selectedAccountsData.filter(a => !a.isGroupParent && !a.parentAccountId);
                          if (selectable.length < 2) return;
                          const firstWithPhone = selectable.find(a => a.phone);
                          setGroupPhone(phoneDigits(firstWithPhone?.phone));
                          setGroupParentName("");
                          setGroupDialogOpen(true);
                        }}
                        className={selectedAccountsData.filter(a => !a.isGroupParent && !a.parentAccountId).length < 2 ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
                        disabled={selectedAccountsData.filter(a => !a.isGroupParent && !a.parentAccountId).length < 2}
                      >
                        Group Accounts
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={selectedAccounts.size === 0 ? undefined : () => setMassTextDialogOpen(true)}
                        className={selectedAccounts.size === 0 ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
                        disabled={selectedAccounts.size === 0}
                      >
                        Send Text
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={selectedAccounts.size === 0 ? undefined : () => handleBulkDelete()}
                        className={selectedAccounts.size === 0 ? "opacity-50 cursor-not-allowed text-red-400" : "cursor-pointer text-red-600 hover:text-red-700"}
                        disabled={selectedAccounts.size === 0}
                      >
                        Delete Selected
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={refreshPhones.isPending ? undefined : () => refreshPhones.mutate()}
                        className={refreshPhones.isPending ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
                        disabled={refreshPhones.isPending}
                        data-testid="menu-refresh-phones"
                      >
                        {refreshPhones.isPending && <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />}
                        Refresh phone numbers from QuickBooks
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
            </div>
            
            <div className="flex gap-2 items-center flex-shrink-0 ml-auto">
              {(requestedCount > 0 || showOnlyRequested) && (
                <button
                  type="button"
                  onClick={() => setShowOnlyRequested(v => !v)}
                  aria-pressed={showOnlyRequested}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 h-8 text-xs font-medium transition-colors ${
                    showOnlyRequested
                      ? "bg-amber-500 border-amber-500 text-white hover:bg-amber-600"
                      : "bg-amber-50 border-amber-200 text-amber-800 hover:bg-amber-100"
                  }`}
                  title={showOnlyRequested ? "Show all accounts" : "Show only account requests awaiting review"}
                  data-testid="button-filter-requested"
                >
                  <span className={`h-1.5 w-1.5 rounded-full ${showOnlyRequested ? "bg-white" : "bg-amber-500"}`} />
                  Requested
                  <span className={`rounded-full px-1.5 ${showOnlyRequested ? "bg-white/20" : "bg-amber-200/70"}`}>{requestedCount}</span>
                </button>
              )}
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen} key={editingAccount?.id || 'new'}>
              <DialogTrigger asChild>
                <Button 
                  size="icon"
                  onClick={() => { 
                    setEditingAccount(null); 
                    form.reset(newAccountForm);
                    if (products.length > 0) {
                      const defaultMenuConfig = {
                        categories: categories.reduce((acc, category) => {
                          acc[category.id.toString()] = true;
                          return acc;
                        }, {} as Record<string, boolean>),
                        products: products.reduce((acc, product) => {
                          acc[product.id.toString()] = true;
                          return acc;
                        }, {} as Record<string, boolean>)
                      };
                      form.setValue("customMenu", defaultMenuConfig);
                    }
                    setMenuConfigOpen(categories.reduce((acc, category) => {
                      acc[category.id] = false;
                      return acc;
                    }, {} as Record<number, boolean>));
                  }}
                  className="h-8 w-8 p-0 bg-cn-brand-green hover:bg-[hsl(140,27%,88%)] hover:text-cn-brand-green text-white rounded-md flex-shrink-0"
                  data-testid="button-add-account"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[500px] p-0">
            <DialogHeader className="border-b border-gray-100 bg-gray-50/50 p-6 pb-4">
              <DialogTitle className="flex items-center gap-2 text-lg font-medium">
                <Plus className="h-5 w-5 text-cn-brand-green" />
                {editingAccount ? "Edit Account" : "Create New Account"}
              </DialogTitle>
              <p className="text-sm text-gray-600 mt-2">
                {editingAccount ? "Update account information and settings" : "Add a new customer account to the system"}
              </p>
            </DialogHeader>
            {/* noValidate: accountFormSchema does the checking. The browser's own email check
                would refuse a stored email the admin never touched. */}
            <form noValidate onSubmit={form.handleSubmit(handleSubmit, (errors) => console.error('🚫 RHF validation failed:', errors))} className="p-6 space-y-6">
              <div className="space-y-2">
                <Label htmlFor="name" className="font-medium">Account Name *</Label>
                <Input
                  id="name"
                  {...form.register("name")}
                  placeholder="Customer name or business"
                  className="bg-[rgb(243,244,246)]"
                  data-testid="input-account-name"
                />
                {form.formState.errors.name && (
                  <p className="text-sm text-red-600">{form.formState.errors.name.message}</p>
                )}
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="phone" className="font-medium">Phone Number *</Label>
                <Input
                  id="phone"
                  {...form.register("phone")}
                  placeholder="555-555-5555"
                  maxLength={12}
                  className="bg-[rgb(243,244,246)]"
                  onChange={(e) => {
                    // Dashed on screen; handleSubmit / accountUpdateBody send the digits.
                    form.setValue("phone", dashedPhone(e.target.value));
                  }}
                  data-testid="input-phone-number"
                />
                {form.formState.errors.phone && (
                  <p className="text-sm text-red-600">{form.formState.errors.phone.message}</p>
                )}
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="emailAddress" className="font-medium">Email Address</Label>
                <Input
                  id="emailAddress"
                  type="email"
                  {...form.register("emailAddress")}
                  placeholder="customer@example.com"
                  className="bg-[rgb(243,244,246)]"
                  data-testid="input-email-address"
                />
                {form.formState.errors.emailAddress && (
                  <p className="text-sm text-red-600">{form.formState.errors.emailAddress.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="customUrl" className="font-medium">Custom URL</Label>
                <Input
                  id="customUrl"
                  {...form.register("customUrl")}
                  placeholder={editingAccount ? "None" : "Auto-generated from name"}
                  disabled
                  className="bg-gray-100 text-gray-600"
                  data-testid="input-custom-url"
                />
                <div className={`rounded-lg p-3 border ${urlWillChange ? "bg-amber-50 border-amber-200" : "bg-blue-50 border-blue-200"}`}>
                  {(shownUrl || !editingAccount) && (
                    <p className="text-sm text-gray-700">
                      <strong>URL:</strong> app.freshorder.io/org/<span className="font-mono text-cn-brand-blue">{orgSlug}</span>/order/<span className="font-mono text-cn-brand-blue">{shownUrl || "your-url"}</span>
                    </p>
                  )}
                  <p className={`text-xs mt-1 ${urlWillChange ? "text-amber-800" : "text-gray-600"}`} data-testid="text-custom-url-hint">
                    {!editingAccount
                      ? "This URL is automatically generated from the account name above"
                      : urlWillChange
                        ? `Saving this name replaces the ordering link with this one.${storedUrl ? " The link the customer has now will stop working." : ""}`
                        : shownUrl
                          ? "The account's ordering link. Renaming the account replaces it with one made from the new name."
                          : "This account has no ordering link. Set a Custom URL on the account's page."}
                  </p>
                </div>
                {form.formState.errors.customUrl && (
                  <p className="text-sm text-red-600">{form.formState.errors.customUrl.message}</p>
                )}
              </div>

              <div className="flex gap-3 pt-4 border-t">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                  className="flex-1"
                  data-testid="button-cancel"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="flex-1 bg-cn-brand-green text-white border border-cn-brand-green hover:bg-[hsl(140,27%,88%)] hover:text-cn-brand-green hover:border-[hsl(140,27%,88%)]"
                  data-testid="button-create-account"
                >
                  {createMutation.isPending || updateMutation.isPending 
                    ? "Saving..." 
                    : editingAccount ? "Update Account" : "Create Account"
                  }
                </Button>
              </div>
            </form>
          </DialogContent>
            </Dialog>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8">Loading accounts...</div>
          ) : (
            <Table>
              <TableHeader className="bg-gray-50">
                <TableRow>
                  <TableHead className="w-[50px] hidden sm:table-cell">
                    <Checkbox
                      checked={selectedAccounts.size === sortedAccounts.length && sortedAccounts.length > 0}
                      onCheckedChange={toggleAllAccounts}
                    />
                  </TableHead>
                  <TableHead>
                    <button
                      onClick={() => handleSort('name')}
                      className="flex items-center hover:text-cn-brand-blue text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      NAME
                      {sortConfig?.key === 'name' && (
                        sortConfig.direction === 'asc' ? <ChevronUp className="ml-1 h-3 w-3" /> : <ChevronDown className="ml-1 h-3 w-3" />
                      )}
                    </button>
                  </TableHead>
                  <TableHead className="hidden sm:table-cell">
                    <button
                      onClick={() => handleSort('phone')}
                      className="flex items-center hover:text-cn-brand-blue text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      PHONE
                      {sortConfig?.key === 'phone' && (
                        sortConfig.direction === 'asc' ? <ChevronUp className="ml-1 h-3 w-3" /> : <ChevronDown className="ml-1 h-3 w-3" />
                      )}
                    </button>
                  </TableHead>
                  <TableHead className="text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">CUSTOM URL</TableHead>
                  {showTypeColumn && (
                    <TableHead className="hidden sm:table-cell">
                      <button
                        onClick={() => handleSort('customerType')}
                        className="flex items-center hover:text-cn-brand-blue text-xs font-medium text-gray-500 uppercase tracking-wider"
                        data-testid="button-sort-customer-type"
                      >
                        TYPE
                        {sortConfig?.key === 'customerType' && (
                          sortConfig.direction === 'asc' ? <ChevronUp className="ml-1 h-3 w-3" /> : <ChevronDown className="ml-1 h-3 w-3" />
                        )}
                      </button>
                    </TableHead>
                  )}
                  <TableHead>
                    <button
                      onClick={() => handleSort('isActive')}
                      className="flex items-center hover:text-cn-brand-blue text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      STATUS
                      {sortConfig?.key === 'isActive' && (
                        sortConfig.direction === 'asc' ? <ChevronUp className="ml-1 h-3 w-3" /> : <ChevronDown className="ml-1 h-3 w-3" />
                      )}
                    </button>
                  </TableHead>
                  <TableHead className="hidden sm:table-cell">
                    <button
                      onClick={() => handleSort('createdAt')}
                      className="flex items-center hover:text-cn-brand-blue text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      CREATED
                      {sortConfig?.key === 'createdAt' && (
                        sortConfig.direction === 'asc' ? <ChevronUp className="ml-1 h-3 w-3" /> : <ChevronDown className="ml-1 h-3 w-3" />
                      )}
                    </button>
                  </TableHead>
                  <TableHead>
                    <button
                      onClick={() => handleSort('lastOrder')}
                      className="flex items-center hover:text-cn-brand-blue text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      LAST ORDER
                      {sortConfig?.key === 'lastOrder' && (
                        sortConfig.direction === 'asc' ? <ChevronUp className="ml-1 h-3 w-3" /> : <ChevronDown className="ml-1 h-3 w-3" />
                      )}
                    </button>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedAccounts.map((account) => (
                  <TableRow key={account.id} className={account.isGroupParent ? "bg-gray-50/80" : account.parentAccountId ? "bg-white" : ""}>
                    <TableCell className="hidden sm:table-cell">
                      {!account.isGroupParent && (
                        <Checkbox
                          checked={selectedAccounts.has(account.id)}
                          onCheckedChange={() => toggleAccountSelection(account.id)}
                        />
                      )}
                    </TableCell>
                    <TableCell className="font-medium max-w-32">
                      {account.isGroupParent ? (
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-gray-700 truncate" title={account.name}>{account.name}</span>
                          <button
                            onClick={async () => {
                              try {
                                await apiRequest("POST", "/api/accounts/ungroup", { parentAccountId: account.id });
                                queryClient.invalidateQueries({ queryKey: ['/api/accounts'] });
                                toast({ title: "Group dissolved", description: "Accounts have been ungrouped." });
                              } catch {
                                toast({ title: "Error", description: "Failed to ungroup accounts.", variant: "destructive" });
                              }
                            }}
                            className="text-xs text-gray-400 hover:text-red-500 shrink-0"
                            title="Dissolve group"
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </div>
                      ) : (
                      <ContextMenu>
                        <ContextMenuTrigger asChild>
                          <button
                            onClick={() => setLocation(`${adminBase}/accounts/${account.id}`)}
                            className={`text-cn-brand-blue hover:text-cn-brand-blue-hover hover:underline cursor-pointer text-left truncate block w-full ${account.parentAccountId ? "pl-6" : ""}`}
                            title={account.name}
                          >
                            {account.parentAccountId && <span className="text-gray-300 mr-1">└</span>}
                            {account.name}
                            {threadByAccount.has(account.id) && (
                              <Bot
                                className="inline-block ml-1.5 h-3.5 w-3.5 align-text-bottom text-cn-brand-green"
                                aria-label="Clyde thread active"
                                data-testid={`badge-clyde-thread-${account.id}`}
                              />
                            )}
                          </button>
                        </ContextMenuTrigger>
                        <ContextMenuContent className="w-48">
                          <ContextMenuItem
                            onClick={() => setLocation(`${adminBase}/accounts/${account.id}`)}
                            className="cursor-pointer"
                          >
                            <ExternalLink className="mr-2 h-4 w-4" />
                            View Account
                          </ContextMenuItem>
                          <ContextMenuItem
                            onClick={() => {
                              handleEdit(account);
                              setDialogOpen(true);
                            }}
                            className="cursor-pointer"
                          >
                            <Edit className="mr-2 h-4 w-4" />
                            Edit Account
                          </ContextMenuItem>
                          {account.customUrl && (
                            <ContextMenuItem
                              onClick={() => window.open(`/org/${orgSlug}/order/${account.customUrl}`, '_blank')}
                              className="cursor-pointer"
                            >
                              <Link2 className="mr-2 h-4 w-4" />
                              Open Order Page
                            </ContextMenuItem>
                          )}
                          {agentEnabled && (threadByAccount.has(account.id) ? (
                            <ContextMenuItem
                              onClick={() => setLocation(consoleThreadPath(adminBase, threadByAccount.get(account.id)!.agentConversationId))}
                              className="cursor-pointer"
                              data-testid={`menu-open-clyde-thread-${account.id}`}
                            >
                              <Bot className="mr-2 h-4 w-4" />
                              Open thread
                            </ContextMenuItem>
                          ) : account.parentAccountId ? (
                            // Standalone accounts only (the group parent row has no menu at all).
                            // The item is disabled, so the hint sits on a wrapper the pointer can reach.
                            <div title="Standalone accounts only — a location in a group can't order by text yet.">
                              <ContextMenuItem disabled data-testid={`menu-start-clyde-thread-${account.id}`}>
                                <Bot className="mr-2 h-4 w-4" />
                                <span>
                                  Start a Clyde thread
                                  <span className="block text-[11px] text-gray-400">Standalone accounts only</span>
                                </span>
                              </ContextMenuItem>
                            </div>
                          ) : (
                            <ContextMenuItem
                              onClick={() => setClydeAccount({ id: account.id, name: account.name, phone: account.phone })}
                              className="cursor-pointer"
                              data-testid={`menu-start-clyde-thread-${account.id}`}
                            >
                              <Bot className="mr-2 h-4 w-4" />
                              Start a Clyde thread
                            </ContextMenuItem>
                          ))}
                          <ContextMenuSeparator />
                          {account.status === 'pending' ? (
                            <ContextMenuItem
                              onClick={() => setLocation(`${adminBase}/accounts/${account.parentAccountId ?? account.id}`)}
                              className="cursor-pointer text-amber-700"
                            >
                              <Settings className="mr-2 h-4 w-4" />
                              Review request
                            </ContextMenuItem>
                          ) : (
                            <ContextMenuItem
                              onClick={() => handleStatusToggle(account.id, !account.isActive)}
                              className="cursor-pointer"
                            >
                              <Settings className="mr-2 h-4 w-4" />
                              {account.isActive ? "Make Inactive" : "Make Active"}
                            </ContextMenuItem>
                          )}
                          <ContextMenuSeparator />
                          <ContextMenuItem
                            onClick={() => handleBulkDelete([account.id])}
                            className="cursor-pointer text-red-600"
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete Account
                          </ContextMenuItem>
                        </ContextMenuContent>
                      </ContextMenu>
                      )}
                    </TableCell>
                    <TableCell className="hidden sm:table-cell">{account.isGroupParent ? account.phone : account.phone}</TableCell>
                    <TableCell className="max-w-36 hidden sm:table-cell">
                      {account.customUrl ? (
                        <button
                          onClick={() => window.open(`/org/${orgSlug}/order/${account.customUrl}`, '_blank')}
                          className="text-sm bg-gray-100 hover:bg-gray-200 px-2 py-1 rounded font-mono cursor-pointer transition-colors hover:text-cn-brand-blue text-left truncate block w-full"
                          title={`/org/${orgSlug}/order/${account.customUrl}`}
                        >
                          /{account.customUrl}
                        </button>
                      ) : (
                        <span className="text-gray-400">None</span>
                      )}
                    </TableCell>
                    {showTypeColumn && (
                      <TableCell className="hidden sm:table-cell text-sm" data-testid={`text-account-type-${account.id}`}>
                        {account.isGroupParent ? (
                          <span className="text-gray-400">By location</span>
                        ) : account.customerTypeId != null ? (
                          customerTypeNames.get(account.customerTypeId) ?? <span className="text-gray-400">—</span>
                        ) : (
                          <span className="text-gray-400">Default</span>
                        )}
                      </TableCell>
                    )}
                    <TableCell>
                      {account.status === 'pending' ? (
                        <button
                          type="button"
                          onClick={() => setLocation(`${adminBase}/accounts/${account.parentAccountId ?? account.id}`)}
                          className="bg-amber-100 text-amber-800 hover:bg-amber-200 rounded-md px-2 py-1 h-auto text-xs font-medium cursor-pointer"
                          title={`Requested${account.requesterName ? ` by ${account.requesterName}` : ""}${account.requestedAt ? ` on ${new Date(account.requestedAt).toLocaleDateString()}` : ""} — click to review`}
                          data-testid={`badge-requested-${account.id}`}
                        >
                          Requested
                        </button>
                      ) : (
                      <Popover open={statusPopoverOpen === account.id} onOpenChange={(open) => setStatusPopoverOpen(open ? account.id : null)}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className={`${
                              account.isActive 
                                ? "bg-[var(--status-procured-bg)] text-cn-brand-green hover:bg-[var(--status-procured-bg)]" 
                                : "bg-orange-100 text-[#ea580b] hover:bg-orange-200"
                            } p-1 h-auto text-xs cursor-pointer`}
                          >
                            {account.isActive ? "Active" : "Inactive"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent 
                          className="w-48 p-2"
                          onPointerDownOutside={(e) => {
                            e.stopPropagation();
                          }}
                          onInteractOutside={(e) => {
                            e.stopPropagation();
                          }}
                        >
                          <div className="space-y-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="w-full justify-start text-xs"
                              onClick={() => handleStatusToggle(account.id, true)}
                              disabled={account.isActive}
                            >
                              Mark as Active
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="w-full justify-start text-xs"
                              onClick={() => handleStatusToggle(account.id, false)}
                              disabled={!account.isActive}
                            >
                              Mark as Inactive
                            </Button>
                          </div>
                        </PopoverContent>
                      </Popover>
                      )}
                    </TableCell>
                    <TableCell className="hidden sm:table-cell">
                      {new Date(account.createdAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      {getLastOrderInfo(account.id)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
            </CardContent>
          </Card>

          {/* Order Slip Modal */}
          <OrderSlip
            order={selectedOrder}
            isOpen={isOrderSlipOpen}
            onClose={() => setIsOrderSlipOpen(false)}
          />

          <StartClydeThreadDialog
            account={clydeAccount}
            open={clydeAccount != null}
            onOpenChange={(open) => { if (!open) setClydeAccount(null); }}
          />

          {/* Merge Accounts Dialog */}
          <Dialog open={mergeDialogOpen} onOpenChange={setMergeDialogOpen}>
            <DialogContent className="sm:max-w-[425px]">
              <DialogHeader>
                <DialogTitle>Merge Accounts</DialogTitle>
                <DialogDescription>
                  Select which account to keep. All orders from the other accounts will be transferred to this account.
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="space-y-2">
                  <Label>Keep account:</Label>
                  <Select
                    value={mergeTargetAccount?.toString() || ""}
                    onValueChange={(value) => setMergeTargetAccount(parseInt(value))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select account to keep" />
                    </SelectTrigger>
                    <SelectContent>
                      {selectedAccountsData.map((account) => (
                        <SelectItem key={account.id} value={account.id.toString()}>
                          {account.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="text-sm text-gray-600">
                  <p className="font-medium mb-2">The following will happen:</p>
                  <ul className="list-disc pl-5 space-y-1">
                    <li>All orders will be transferred to the selected account</li>
                    <li>Other accounts will be deleted after merging</li>
                    <li>This action cannot be undone</li>
                  </ul>
                </div>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => {
                    setMergeDialogOpen(false);
                    setMergeTargetAccount(null);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleMergeAccounts}
                  disabled={!mergeTargetAccount || mergeAccountsMutation.isPending}
                  className="bg-cn-brand-green hover:bg-cn-brand-green-hover"
                >
                  {mergeAccountsMutation.isPending ? "Merging..." : "Merge Accounts"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Group Accounts Dialog */}
          <Dialog open={groupDialogOpen} onOpenChange={setGroupDialogOpen}>
            <DialogContent className="sm:max-w-[425px]">
              <DialogHeader>
                <DialogTitle>Group Accounts</DialogTitle>
                <DialogDescription>
                  Create a shared login for multiple accounts. The contact person will choose which account to order for after entering their PIN.
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="space-y-2">
                  <Label>Contact Name</Label>
                  <Input
                    placeholder="e.g. Tony Smith"
                    value={groupParentName}
                    onChange={(e) => setGroupParentName(e.target.value)}
                  />
                  <p className="text-xs text-gray-500">The name that appears as the group header</p>
                </div>
                <div className="space-y-2">
                  <Label>Phone Number</Label>
                  <Input
                    type="tel"
                    placeholder="555-555-5555"
                    value={dashedPhone(groupPhone)}
                    onChange={(e) => setGroupPhone(phoneDigits(e.target.value))}
                    maxLength={12}
                  />
                  <p className="text-xs text-gray-500">Shared login phone number for all grouped accounts</p>
                </div>
                <div className="space-y-2">
                  <Label>Accounts to group</Label>
                  <div className="bg-gray-50 rounded-md p-3 space-y-1">
                    {selectedAccountsData.filter(a => !a.isGroupParent && !a.parentAccountId).map(acct => (
                      <div key={acct.id} className="text-sm text-gray-700 flex items-center gap-2">
                        <span className="text-gray-300">•</span>
                        {acct.name}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setGroupDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={async () => {
                    const childIds = selectedAccountsData
                      .filter(a => !a.isGroupParent && !a.parentAccountId)
                      .map(a => a.id);
                    try {
                      // groupPhone holds digits; the dashes are only in the input.
                      await apiRequest("POST", "/api/accounts/group", groupAccountsBody(groupParentName, groupPhone, childIds));
                      queryClient.invalidateQueries({ queryKey: ['/api/accounts'] });
                      setGroupDialogOpen(false);
                      setSelectedAccounts(new Set());
                      toast({ title: "Accounts grouped", description: `${childIds.length} accounts are now grouped under "${groupParentName}".` });
                    } catch (err: any) {
                      const msg = err?.message || "Failed to group accounts.";
                      toast({ title: "Error", description: msg, variant: "destructive" });
                    }
                  }}
                  disabled={!groupParentName.trim() || groupPhone.length < 10}
                  className="bg-[#2B5397] hover:bg-[#234680]"
                >
                  Group Accounts
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Mass Text Dialog */}
          <Dialog open={phoneRefresh !== null} onOpenChange={(open) => { if (!open) setPhoneRefresh(null); }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="dialog-phone-refresh">
          <DialogHeader>
            <DialogTitle>Phone numbers from QuickBooks</DialogTitle>
            <DialogDescription>
              Placeholder numbers are replaced with the customer's QuickBooks number (mobile first). A real number is
              never overwritten.
            </DialogDescription>
          </DialogHeader>
          {phoneRefresh && (
            <div className="space-y-4 text-sm">
              <ul className="space-y-1">
                <li data-testid="text-phones-filled"><span className="font-semibold">{phoneRefresh.filled}</span> placeholder {phoneRefresh.filled === 1 ? "number" : "numbers"} replaced — those customers can now sign in with their phone.</li>
                <li>{phoneRefresh.upToDate} already match QuickBooks.</li>
                {phoneRefresh.noPhoneInQuickBooks > 0 && <li>{phoneRefresh.noPhoneInQuickBooks} still have a placeholder: QuickBooks has no usable number for them.</li>}
                {phoneRefresh.notInQuickBooks > 0 && <li>{phoneRefresh.notInQuickBooks} are linked to a customer QuickBooks no longer has.</li>}
              </ul>
              {phoneRefresh.filledWithPin.length > 0 && (
                <div>
                  <p className="font-medium">These already had a PIN — their sign-in number is now the QuickBooks one:</p>
                  <ul className="list-disc pl-5 text-gray-700">{phoneRefresh.filledWithPin.map((a) => <li key={a.accountId}>{a.name}</li>)}</ul>
                </div>
              )}
              {phoneRefresh.differs.length > 0 && (
                <div data-testid="list-phones-differ">
                  <p className="font-medium">QuickBooks has a different number (left as it is — change it on the account if QuickBooks is right):</p>
                  <ul className="divide-y divide-gray-100 rounded-md border">
                    {phoneRefresh.differs.map((a) => (
                      <li key={a.accountId} className="flex flex-wrap justify-between gap-2 p-2">
                        <span>{a.name}</span>
                        <span className="text-gray-600">FreshOrder {formatPhone(a.current)} · QuickBooks {formatPhone(a.quickbooks)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {phoneRefresh.skippedShared.length > 0 && (
                <div>
                  <p className="font-medium">Skipped — their QuickBooks number is already used by another account or contact, so sign-in couldn't tell them apart:</p>
                  <ul className="list-disc pl-5 text-gray-700">{phoneRefresh.skippedShared.map((a) => <li key={a.accountId}>{a.name}</li>)}</ul>
                </div>
              )}
              {phoneRefresh.skippedGroup.length > 0 && (
                <div>
                  <p className="font-medium">Skipped — group accounts share one sign-in phone; set it on the group:</p>
                  <ul className="list-disc pl-5 text-gray-700">{phoneRefresh.skippedGroup.map((a) => <li key={a.accountId}>{a.name}</li>)}</ul>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button type="button" onClick={() => setPhoneRefresh(null)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={massTextDialogOpen} onOpenChange={(open) => {
            setMassTextDialogOpen(open);
            if (!open) setMassTextMessage("");
          }}>
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle>Send Text Message</DialogTitle>
                <DialogDescription>
                  Send an SMS to the selected accounts.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <p className="text-sm text-gray-600">
                  Recipients: {massTextRecipientInfo.totalSelected} account{massTextRecipientInfo.totalSelected === 1 ? '' : 's'}{' '}
                  ({massTextRecipientInfo.uniquePhones} unique phone number{massTextRecipientInfo.uniquePhones === 1 ? '' : 's'})
                </p>
                <div className="space-y-1">
                  <Label>Message</Label>
                  <textarea
                    className="w-full min-h-[120px] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-none"
                    placeholder="Type your message here..."
                    maxLength={1600}
                    value={massTextMessage}
                    onChange={(e) => setMassTextMessage(e.target.value)}
                  />
                  <p className={`text-xs text-right ${massTextMessage.length > 1440 ? 'text-red-500' : 'text-gray-400'}`}>
                    {massTextMessage.length} / 1600 characters
                  </p>
                  <p className="text-xs text-gray-500">
                    Available variables: [account], [org-url], [delivery day], [active order deadline]
                  </p>
                </div>
                {massTextRecipientInfo.noPhone.length > 0 && (
                  <div className="flex items-start gap-2 rounded-md bg-yellow-50 border border-yellow-200 p-3">
                    <AlertTriangle className="h-4 w-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-yellow-800">
                      {massTextRecipientInfo.noPhone.length} account{massTextRecipientInfo.noPhone.length === 1 ? '' : 's'} have no phone number and will be skipped:{' '}
                      {massTextRecipientInfo.noPhone.join(', ')}
                    </p>
                  </div>
                )}
                {massTextRecipientInfo.uniquePhones === 0 && massTextRecipientInfo.noPhone.length === 0 && (
                  <p className="text-sm text-gray-500 text-center py-2">No accounts selected.</p>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setMassTextDialogOpen(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={() => massTextMutation.mutate({ accountIds: Array.from(selectedAccounts), message: massTextMessage })}
                  disabled={massTextMutation.isPending || massTextMessage.trim() === "" || massTextRecipientInfo.uniquePhones === 0}
                  className="bg-[#2B5397] hover:bg-[#234680]"
                >
                  {massTextMutation.isPending ? (
                    <><Loader2 className="h-4 w-4 animate-spin mr-2" />Sending...</>
                  ) : "Send Message"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Bulk Delete Confirmation Dialog */}
          <Dialog open={bulkDeleteDialogOpen} onOpenChange={setBulkDeleteDialogOpen}>
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-yellow-500" />
                  Delete Accounts
                </DialogTitle>
                <DialogDescription>
                  Review which accounts can be deleted.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                {deletionInfo.canDelete.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-green-700 mb-2">
                      Can be deleted ({deletionInfo.canDelete.length} account{deletionInfo.canDelete.length === 1 ? '' : 's'}):
                    </p>
                    <ul className="text-sm space-y-1">
                      {deletionInfo.canDelete.map(id => {
                        const account = accounts?.find(a => a.id === id);
                        return (
                          <li key={id} className="flex items-center gap-2">
                            <span className="text-green-600">✓</span>
                            {account?.name || 'Unknown'}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
                
                {deletionInfo.cannotDelete.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-red-700 mb-2">
                      Cannot be deleted (have order history):
                    </p>
                    <ul className="text-sm space-y-1">
                      {deletionInfo.cannotDelete.map(({ id, name, orderCount }) => (
                        <li key={id} className="flex items-center gap-2">
                          <span className="text-red-600">✗</span>
                          {name} ({orderCount} order{orderCount === 1 ? '' : 's'})
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {deletionInfo.canDelete.length === 0 && (
                  <div className="text-center py-4 text-gray-500">
                    None of the selected accounts can be deleted because they all have order history.
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => {
                    setBulkDeleteDialogOpen(false);
                    setDeletionInfo({ canDelete: [], cannotDelete: [] });
                  }}
                >
                  Cancel
                </Button>
                {deletionInfo.canDelete.length > 0 && (
                  <Button
                    variant="destructive"
                    onClick={confirmBulkDelete}
                    disabled={bulkDeleteMutation.isPending}
                  >
                    {bulkDeleteMutation.isPending ? "Deleting..." : `Delete ${deletionInfo.canDelete.length} Account${deletionInfo.canDelete.length === 1 ? '' : 's'}`}
                  </Button>
                )}
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* QuickBooks Customer Link Dialog */}
          <Dialog open={qbLinkDialogOpen} onOpenChange={(open) => {
            if (!open) {
              // If closing without confirming, still navigate to account page
              if (qbLinkingAccount) {
                setLocation(`${adminBase}/accounts/${qbLinkingAccount.id}`);
              }
              setQbLinkDialogOpen(false);
              setQbLinkingAccount(null);
              setQbCustomerMatches([]);
            }
          }}>
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <SiQuickbooks className="h-5 w-5 text-[#2ca01c]" />
                  Link to QuickBooks Customer
                </DialogTitle>
                <DialogDescription>
                  {qbLinkingAccount && (
                    <>
                      We found potential matches for <strong>{qbLinkingAccount.name}</strong> in QuickBooks. 
                      Select a customer to link, or skip to continue without linking.
                    </>
                  )}
                </DialogDescription>
              </DialogHeader>
              
              {isLoadingQbMatches ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                  <span className="ml-2 text-gray-500">Searching QuickBooks...</span>
                </div>
              ) : (
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  <RadioGroup value={qbLinkSelection} onValueChange={setQbLinkSelection}>
                    {qbCustomerMatches.map((match) => (
                      <div 
                        key={match.id}
                        className={`flex items-start space-x-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                          qbLinkSelection === match.id ? 'border-[#2ca01c] bg-green-50' : 'border-gray-200 hover:border-gray-300'
                        }`}
                        onClick={() => setQbLinkSelection(match.id)}
                      >
                        <RadioGroupItem value={match.id} id={`qb-match-${match.id}`} className="mt-1" />
                        <Label htmlFor={`qb-match-${match.id}`} className="flex-1 cursor-pointer">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Link2 className="h-4 w-4 text-[#2ca01c]" />
                            <span className="font-medium">{match.displayName}</span>
                            {match.matchReasons && match.matchReasons.map((reason, idx) => (
                              <Badge 
                                key={idx}
                                variant="outline" 
                                className={
                                  reason === 'phone match' 
                                    ? 'bg-green-50 text-green-700 border-green-200' 
                                    : reason === 'email match'
                                      ? 'bg-purple-50 text-purple-700 border-purple-200'
                                      : reason === 'exact name'
                                        ? 'bg-blue-50 text-blue-700 border-blue-200'
                                        : 'bg-yellow-50 text-yellow-700 border-yellow-200'
                                }
                              >
                                {reason}
                              </Badge>
                            ))}
                          </div>
                          {(match.companyName || match.email || match.phone) && (
                            <div className="text-sm text-gray-500 mt-1">
                              {match.companyName && <span>{match.companyName}</span>}
                              {match.companyName && (match.email || match.phone) && <span> · </span>}
                              {match.email && <span>{match.email}</span>}
                              {match.email && match.phone && <span> · </span>}
                              {match.phone && <span>{match.phone}</span>}
                            </div>
                          )}
                        </Label>
                      </div>
                    ))}
                    
                    {/* Skip option */}
                    <div 
                      className={`flex items-start space-x-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        qbLinkSelection === 'skip' ? 'border-gray-400 bg-gray-50' : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => setQbLinkSelection('skip')}
                    >
                      <RadioGroupItem value="skip" id="qb-match-skip" className="mt-1" />
                      <Label htmlFor="qb-match-skip" className="flex-1 cursor-pointer">
                        <div className="flex items-center gap-2">
                          <UserPlus className="h-4 w-4 text-gray-500" />
                          <span className="font-medium">Skip linking for now</span>
                        </div>
                        <div className="text-sm text-gray-500 mt-1">
                          You can link to QuickBooks later from the account page
                        </div>
                      </Label>
                    </div>
                  </RadioGroup>
                </div>
              )}
              
              <DialogFooter>
                <Button
                  onClick={handleQbLinkConfirm}
                  disabled={isLinkingToQb}
                  className={qbLinkSelection !== 'skip' ? 'bg-[#2ca01c] hover:bg-[#228617]' : ''}
                  data-testid="btn-confirm-qb-link"
                >
                  {isLinkingToQb ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Linking...
                    </>
                  ) : qbLinkSelection === 'skip' ? (
                    'Continue Without Linking'
                  ) : (
                    'Link to QuickBooks'
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
    </div>
  );
}